export type KeysToCheck =
  | 'customer'
  | 'phoneNumber'
  | 'deliveryAddress'
  | 'deliveryContact'
  | 'options'
  | 'planDetails'
  | 'plan'
  | 'notificationContact'
  | 'identificationType';

interface Customer {
  birthdate?: string;
  contactPhoneNumber?: string;
  familyName?: string;
  gender?: string;
  givenName?: string;
}

interface DeliveryAddress {
  city?: string;
  fullName?: string;
  postCode?: string;
  stateOrProvince?: string;
  street1?: string;
}

interface DeliveryContact {
  phoneNumber?: string;
  name?: string;
}

interface Option {
  type: string;
}

interface CustomerInfos {
  customer?: Customer;
  deliveryAddress?: DeliveryAddress;
  deliveryContact?: DeliveryContact;
  identificationType?: string;
  notificationContact?: string;
  options?: {
    creditable: Option[];
  };
  phoneNumber?: string;
  planDetails?: {
    options?: Option[];
  };
}

export const checkOrderConsistency = (customerInfos: CustomerInfos = {}, keysToCheck: KeysToCheck[]): string => {
  let error = '';

  const customerInfosChecker = {
    customer: (): void => {
      if (!customerInfos?.customer?.gender) {
        error = 'NO_CUSTOMER_GENDER';
      } else if (!customerInfos?.customer?.givenName) {
        error = 'NO_CUSTOMER_GIVEN_NAME';
      } else if (!customerInfos?.customer?.birthdate) {
        error = 'NO_CUSTOMER_BIRTHDATE';
      } else if (!customerInfos?.customer?.familyName) {
        error = 'NO_CUSTOMER_FAMILYNAME';
      } else if (!customerInfos?.customer?.contactPhoneNumber) {
        error = 'NO_CUSTOMER_CONTACT_PHONE_NUMBER';
      }
    },
    phoneNumber: (): void => {
      if (!customerInfos.phoneNumber) {
        error = 'NO_PHONE_NUMBER';
      }
    },
    deliveryAddress: (): void => {
      if (!customerInfos.deliveryAddress) {
        error = 'NO_DELIVERY_ADDRESS';
      } else {
        const { city, fullName, postCode, stateOrProvince, street1 } = customerInfos.deliveryAddress;
        if (
          [city, fullName, postCode, stateOrProvince, street1].some(deliveryAddressDetail => !deliveryAddressDetail)
        ) {
          error = 'DELIVERY_ADDRESS_INCOMPLETE';
        }
      }
    },
    deliveryContact: (): void => {
      if (!customerInfos.deliveryContact) {
        error = 'NO_DELIVERY_CONTACT';
      } else {
        const { phoneNumber: contactPhoneNumber, name } = customerInfos.deliveryContact;

        if (!name || !contactPhoneNumber) {
          error = 'DELIVERY_CONTACT_INCOMPLETE';
        }
      }
    },
    options: (): void => {
      if (!customerInfos.options) {
        error = 'NO_PLAN';
      } else if (Array.isArray(customerInfos.options.creditable)) {
        customerInfosChecker.plan(customerInfos.options.creditable);
      } else {
        error = 'PLAN_INCOMPLETE';
      }
    },
    planDetails: (): void => {
      if (!customerInfos.planDetails) {
        error = 'NO_PLAN';
      } else if (customerInfos.planDetails) {
        customerInfosChecker.plan(customerInfos.planDetails.options);
      } else {
        error = 'PLAN_INCOMPLETE';
      }
    },
    plan: (options?: Option[]): void => {
      if (!options) {
        error = 'NO_PLAN';
      } else {
        let requiredOptions = ['VOICE', 'DATA', 'SMS'];
        for (const option of options) {
          requiredOptions = requiredOptions.filter(requiredOption => requiredOption === option.type);
        }

        if (requiredOptions.length) {
          error = 'PLAN_INCOMPLETE';
        }
      }
    },
    notificationContact: (): void => {
      if (!customerInfos?.notificationContact) {
        error = 'NO_NOTIFICATION_CONTACT';
      }
    },
    identificationType: (): void => {
      if (!customerInfos?.identificationType) {
        error = 'NO_IDENTIFICATION_TYPE';
      } else {
        const availableDocuments = [
          'BIRTH_CERTIFICATE',
          'DRIVER_LICENCE',
          'IDENTITY_CARD',
          'PASSPORT',
          'PICTURE_ID',
          'SELFIE',
          'SOCIAL_SECURITY_NUMBER',
        ];
        if (!availableDocuments.includes(customerInfos.identificationType)) {
          error = 'IDENTIFICATION_TYPE_INVALID';
        }
      }
    },
  };

  for (const key of keysToCheck) {
    if (customerInfosChecker[key]) {
      customerInfosChecker[key]();
    }
    if (error) {
      return error;
    }
  }

  return 'ORDER_OK';
};
